import { FilterId } from '../../../../filters/types/filters';
import { toDisplayName } from './talent-discovery-column-mappings';

export const flightRiskSortingFn = (
  rowA: { getValue: (columnId: string) => unknown },
  rowB: { getValue: (columnId: string) => unknown },
  columnId: string
): number => {
  const normalize = (v: unknown): number => {
    const s = String(v ?? '')
      .trim()
      .toLowerCase();
    if (s === 'very low') return 0;
    if (s === 'low') return 1;
    if (s === 'medium') return 2;
    if (s === 'high') return 3;
    if (s === 'very high') return 4;
    return Number.POSITIVE_INFINITY;
  };
  const a = normalize(rowA.getValue(columnId));
  const b = normalize(rowB.getValue(columnId));
  return a - b;
};

export const prestigeSortingFn = (
  rowA: { getValue: (columnId: string) => unknown },
  rowB: { getValue: (columnId: string) => unknown },
  columnId: string
): number => {
  const normalize = (v: unknown): number => {
    const s = String(v ?? '')
      .trim()
      .toLowerCase();
    if (s === 'very low') return 0;
    if (s === 'low') return 1;
    if (s === 'medium') return 2;
    if (s === 'high') return 3;
    if (s === 'very high') return 4;
    return Number.POSITIVE_INFINITY;
  };
  const a = normalize(rowA.getValue(columnId));
  const b = normalize(rowB.getValue(columnId));
  return a - b;
};

/** Same tier labels/order as prestige and flight risk (Very Low → Very High). */
export const remoteSuitabilitySortingFn = prestigeSortingFn;

export const numericSortingFn = (
  rowA: { getValue: (columnId: string) => unknown },
  rowB: { getValue: (columnId: string) => unknown },
  columnId: string
): number => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  const numA = a === null || a === '' ? NaN : Number(a);
  const numB = b === null || b === '' ? NaN : Number(b);

  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  if (isNaN(numA) && isNaN(numB)) return 0;
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  if (isNaN(numA)) return 1;
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  if (isNaN(numB)) return -1;

  return numA - numB;
};

const COLUMN_DISPLAY_OVERRIDES: Record<string, string> = {
  Company: 'Company Name',
  'Market share': 'Market Share',
  'Postings active': 'Active Postings',
  Overall: 'Sentiment Rating',
  'Demand supply': 'Sentiment Rating',
};

export const getTableColumnHeader = (
  key: string,
  primaryDimension?: string
) => {
  if (key === 'Primary') {
    const resolved = getPrimaryColumnHeader(primaryDimension);
    return COLUMN_DISPLAY_OVERRIDES[resolved] ?? resolved;
  }

  if (COLUMN_DISPLAY_OVERRIDES[key]) {
    return COLUMN_DISPLAY_OVERRIDES[key];
  }

  if (key === 'From') {
    return 'Inflow From';
  }

  if (key === 'Transitions outflow_To') {
    return 'Outflow To';
  }

  if (key === 'Transitions inflow') {
    return 'Inflow';
  }

  if (key === 'Transitions outflow') {
    return 'Outflow';
  }

  if (
    [
      'Time to fill_Date',
      'Postings active_Date',
      'Postings new_Date',
      'Postings removed_Date',
      'Posting salary_Date',
    ].includes(key)
  ) {
    return 'Postings Date';
  }

  return toDisplayName(key);
};

export const TD_DEFAULT_COLUMNS = [
  'first_name',
  'last_name',
  'email',
  'current_title',
  'current_company',
  'salary_total',
  'user_location',
];

export const getPrimaryColumnHeader = (primaryDimension?: string): string => {
  switch (primaryDimension) {
    case FilterId.COMPANY:
      return 'Company';
    case FilterId.GEOGRAPHY:
      return 'Geography';
    case FilterId.ROLE:
      return 'Role';
    default:
      return 'Primary';
  }
};
